import { Routes, Route } from 'react-router-dom';
import { AmplifyInit } from '@/components/amplify-init';
import { ThemeProvider } from '@/providers/theme-provider';
import { AuthProvider } from '@/providers/auth-provider';
import { AuthGuard } from '@/components/auth/auth-guard';
import { QueryProvider } from '@/providers/query-provider';
import { OrgProvider } from '@/providers/org-provider';
import { AppLayout } from '@/layouts/app-layout';
import { AuthLayout } from '@/layouts/auth-layout';

// Pages
import { LandingPage } from '@/pages/landing';
import { LoginPage } from '@/pages/login';
import { SignupPage } from '@/pages/signup';
import { ForgotPasswordPage } from '@/pages/forgot-password';
import { DashboardPage } from '@/pages/dashboard';
import { ProjectsPage } from '@/pages/projects';
import { NewProjectPage } from '@/pages/projects-new';
import { ProjectDetailPage } from '@/pages/project-detail';
import { ScansPage } from '@/pages/scans';
import { NewScanPage } from '@/pages/scans-new';
import { ScanDetailPage } from '@/pages/scan-detail';
import { FindingsPage } from '@/pages/findings';
import { LearnPage } from '@/pages/learn';
import { LessonPage } from '@/pages/lesson';
import { OnboardingPage } from '@/pages/onboarding';
import { SettingsPage } from '@/pages/settings';
import { NotificationsPage } from '@/pages/notifications';
import { ScoringGuidePage } from '@/pages/scoring-guide';
import { QuantumReadinessGuidePage } from '@/pages/quantum-readiness-guide';
import { FindingsLifecycleGuidePage } from '@/pages/findings-lifecycle-guide';
import { SecurityControlsPage } from '@/pages/admin/security-controls';
import { ProjectSettingsPage } from '@/pages/project-settings';
import { LabsPage } from '@/pages/labs';
import { AgentsPage } from '@/pages/agents';
import { AgentDetailPage } from '@/pages/agent-detail';
import { AgentRunDetailPage } from '@/pages/agent-run-detail';
import { ProjectReportPage } from '@/pages/project-report';
import { OrgNewPage } from '@/pages/org-new';
import { OrgSettingsPage } from '@/pages/org-settings';
import { PortfoliosPage } from '@/pages/portfolios';
import { AcceptInvitationPage } from '@/pages/accept-invitation';
import { PostLoginRedirect } from '@/pages/post-login-redirect';
import { AdminDashboardPage } from '@/pages/admin/dashboard';
import { AdminOrgsPage } from '@/pages/admin/orgs';
import { AdminOrgDetailPage } from '@/pages/admin/org-detail';
import { AdminUsersPage } from '@/pages/admin/users';
import { AdminUserDetailPage } from '@/pages/admin/user-detail';
import { AdminAuditLogsPage } from '@/pages/admin/audit-logs';
import { AdminProvidersPage } from '@/pages/admin/providers';
import { AdminScfPage } from '@/pages/admin/scf';
import { TepPage } from '@/pages/tep';
import { AdminGuard } from '@/components/auth/admin-guard';

export function App() {
  return (
    <ThemeProvider>
      <AmplifyInit />
      <AuthProvider>
        <QueryProvider>
          <Routes>
            {/* Public */}
            <Route path="/" element={<LandingPage />} />
            <Route path="/labs" element={<LabsPage />} />

            {/* Auth */}
            <Route element={<AuthLayout />}>
              <Route path="/login" element={<LoginPage />} />
              <Route path="/signup" element={<SignupPage />} />
              <Route path="/forgot-password" element={<ForgotPasswordPage />} />
            </Route>

            {/* Authenticated. OrgProvider mounts inside AuthGuard so /me/orgs
                only fires post-auth and the org list is available to nav. */}
            <Route element={<AuthGuard><OrgProvider><AppLayout /></OrgProvider></AuthGuard>}>
              {/* Post-login landing — redirects to last/first org dashboard. */}
              <Route path="/home" element={<PostLoginRedirect />} />

              {/* Top-level (no org context) */}
              <Route path="/orgs/new" element={<OrgNewPage />} />
              <Route path="/accept/:token" element={<AcceptInvitationPage />} />
              <Route path="/my/learn" element={<LearnPage />} />
              {/* Static lesson — must come BEFORE the :moduleId
                  catch-all so React Router matches the exact path. */}
              <Route path="/my/learn/scoring" element={<ScoringGuidePage />} />
              <Route path="/my/learn/quantum-readiness" element={<QuantumReadinessGuidePage />} />
              <Route path="/my/learn/findings-lifecycle" element={<FindingsLifecycleGuidePage />} />
              <Route path="/my/learn/:moduleId" element={<LessonPage />} />
              <Route path="/onboarding" element={<OnboardingPage />} />
              <Route path="/my/settings" element={<SettingsPage />} />
              <Route path="/my/notifications" element={<NotificationsPage />} />

              {/* Org-scoped */}
              <Route path="/orgs/:orgId/dashboard" element={<DashboardPage />} />
              <Route path="/orgs/:orgId/settings" element={<OrgSettingsPage />} />
              <Route path="/orgs/:orgId/tep" element={<TepPage />} />
              <Route path="/orgs/:orgId/portfolios" element={<PortfoliosPage />} />
              <Route path="/orgs/:orgId/portfolios/:portfolioId" element={<ProjectsPage />} />
              <Route path="/orgs/:orgId/portfolios/:portfolioId/projects/new" element={<NewProjectPage />} />
              <Route path="/orgs/:orgId/portfolios/:portfolioId/projects/:projectId" element={<ProjectDetailPage />} />
              <Route path="/orgs/:orgId/portfolios/:portfolioId/projects/:projectId/scans" element={<ScansPage />} />
              <Route path="/orgs/:orgId/portfolios/:portfolioId/projects/:projectId/scans/new" element={<NewScanPage />} />
              <Route path="/orgs/:orgId/portfolios/:portfolioId/projects/:projectId/scans/:scanId" element={<ScanDetailPage />} />
              <Route path="/orgs/:orgId/portfolios/:portfolioId/projects/:projectId/findings" element={<FindingsPage />} />
              <Route path="/orgs/:orgId/portfolios/:portfolioId/projects/:projectId/report" element={<ProjectReportPage />} />
              <Route path="/orgs/:orgId/portfolios/:portfolioId/projects/:projectId/agents" element={<AgentsPage />} />
              <Route path="/orgs/:orgId/portfolios/:portfolioId/projects/:projectId/agents/runs/:runId" element={<AgentRunDetailPage />} />
              <Route path="/orgs/:orgId/portfolios/:portfolioId/projects/:projectId/agents/:agentType" element={<AgentDetailPage />} />
              <Route path="/orgs/:orgId/portfolios/:portfolioId/projects/:projectId/settings" element={<ProjectSettingsPage />} />

              {/* Platform admin (cross-tenant). AdminGuard 403s non-admins. */}
              <Route element={<AdminGuard />}>
                <Route path="/admin" element={<AdminDashboardPage />} />
                <Route path="/admin/orgs" element={<AdminOrgsPage />} />
                <Route path="/admin/orgs/:orgId" element={<AdminOrgDetailPage />} />
                <Route path="/admin/users" element={<AdminUsersPage />} />
                <Route path="/admin/users/:userId" element={<AdminUserDetailPage />} />
                <Route path="/admin/providers" element={<AdminProvidersPage />} />
                <Route path="/admin/scf" element={<AdminScfPage />} />
                <Route path="/admin/security-controls" element={<SecurityControlsPage />} />
                <Route path="/admin/audit-logs" element={<AdminAuditLogsPage />} />
              </Route>
            </Route>
          </Routes>
        </QueryProvider>
      </AuthProvider>
    </ThemeProvider>
  );
}
